import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import {
    Box,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Chip,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { AdminPageContainer } from '../../components/admin/sharedStyles';
import PageHeader from '../../components/PageHeader';
import RoleDialog from '../../components/admin/RoleDialog';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import RoleUsersDialog from '../../components/admin/RoleUsersDialog';
import { getRoles, createRole, updateRole, deleteRole } from '../../redux/ducks/role';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../redux/slices/alerts';

const FiltersRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const SearchInput = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const LoadingRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const EmptyState = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}));

const MigrateDialogText = styled(DialogContentText)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const UserCountCell = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap',
}));

const AdminRoles = () => {
    const dispatch = useDispatch();
    const [roles, setRoles] = useState([]);
    const [filteredRoles, setFilteredRoles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selectedRole, setSelectedRole] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const [migrateDialogOpen, setMigrateDialogOpen] = useState(false);
    const [migrateUserCount, setMigrateUserCount] = useState(0);
    const [migrateDeletedCount, setMigrateDeletedCount] = useState(0);
    const [migrateToId, setMigrateToId] = useState('');
    const [usersDialogOpen, setUsersDialogOpen] = useState(false);
    const [roleForUsers, setRoleForUsers] = useState(null);

    useEffect(() => {
        loadRoles();
    }, []);

    useEffect(() => {
        filterRoles();
    }, [searchTerm, typeFilter, roles]);

    const loadRoles = () => {
        setLoading(true);
        dispatch(getRoles((data) => {
            setRoles(data || []);
            setLoading(false);
        }));
    };

    const filterRoles = () => {
        let filtered = roles;

        if (typeFilter !== 'all') {
            filtered = filtered.filter(r => r.type === typeFilter);
        }

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (r) =>
                    r.name?.toLowerCase().includes(term) ||
                    r.description?.toLowerCase().includes(term)
            );
        }

        setFilteredRoles(filtered);
    };

    const handleAddClick = () => {
        setDialogMode('create');
        setSelectedRole(null);
        setDialogOpen(true);
    };

    const handleEditClick = (role) => {
        setDialogMode('edit');
        setSelectedRole(role);
        setDialogOpen(true);
    };

    const handleViewUsersClick = (role) => {
        setRoleForUsers(role);
        setUsersDialogOpen(true);
    };

    // roleToDelete and the migrate counts are set here and never cleared on close, so neither dialog
    // blanks its own text mid exit-transition. The next delete click overwrites them.
    const handleDeleteClick = (role) => {
        setRoleToDelete(role);
        setMigrateToId('');
        setMigrateUserCount(0);
        setMigrateDeletedCount(0);
        setConfirmDialogOpen(true);
    };

    const handleSave = (formData, onSuccess) => {
        if (dialogMode === 'create') {
            dispatch(createRole(formData, (response) => {
                dispatch(showAlert({ severity: 'success', message: 'Role created successfully' }));
                loadRoles();
                onSuccess?.();
            }));
        } else {
            dispatch(updateRole(selectedRole._id, formData, (response) => {
                dispatch(showAlert({ severity: 'success', message: 'Role updated successfully' }));
                loadRoles();
                onSuccess?.();
            }));
        }
    };

    const runDeleteRole = (migrateTo) => {
        dispatch(deleteRole(roleToDelete._id, migrateTo, (data, error) => {
            if (error?.status === 409 && error.userCount > 0) {
                setMigrateUserCount(error.userCount);
                setMigrateDeletedCount(error.deletedUserCount || 0);
                setMigrateDialogOpen(true);
                return;
            }
            if (error) return;
            const moved = data?.migratedUsers || 0;
            const msg = moved > 0
                ? `Role deleted; ${moved} user(s) migrated.`
                : 'Role deleted successfully';
            dispatch(showAlert({ severity: 'success', message: msg }));
            setMigrateDialogOpen(false);
            loadRoles();
        }));
    };

    const handleConfirmDelete = () => {
        setConfirmDialogOpen(false);
        runDeleteRole();
    };

    const handleConfirmMigrate = () => {
        if (!migrateToId) return;
        runDeleteRole(migrateToId);
    };

    const migrationCandidates = roleToDelete
        ? roles.filter((r) => r.type === roleToDelete.type && r._id !== roleToDelete._id)
        : [];

    const getTypeColor = (type) => {
        return type === 'Internal' ? 'primary' : 'secondary';
    };

    return (
        <AdminPageContainer>
            <PageHeader
                title="Roles Management"
                subtitle="Manage system roles and assign permissions"
                actions={
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        Add Role
                    </Button>
                }
            />

            <FiltersRow>
                <SearchInput
                    fullWidth
                    placeholder="Search roles by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <ToggleButtonGroup
                    value={typeFilter}
                    exclusive
                    onChange={(e, newValue) => {
                        if (newValue !== null) {
                            setTypeFilter(newValue);
                        }
                    }}
                    size="small"
                >
                    <ToggleButton value="all">All Types</ToggleButton>
                    <ToggleButton value="Internal">Internal</ToggleButton>
                    <ToggleButton value="General">General</ToggleButton>
                </ToggleButtonGroup>
            </FiltersRow>

            {loading ? (
                <LoadingRow>
                    <CircularProgress />
                </LoadingRow>
            ) : filteredRoles.length === 0 ? (
                <EmptyState>
                    <Typography variant="body1" color="text.secondary">
                        {searchTerm || typeFilter !== 'all'
                            ? 'No roles found matching your filters.'
                            : 'No roles created yet. Click "Add Role" to create one.'}
                    </Typography>
                </EmptyState>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Role Name</strong></TableCell>
                                <TableCell><strong>Type</strong></TableCell>
                                <TableCell align="center"><strong>Level</strong></TableCell>
                                <TableCell><strong>Description</strong></TableCell>
                                <TableCell align="center"><strong>Permissions</strong></TableCell>
                                <TableCell align="center"><strong>Assigned Users</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredRoles.map((role) => (
                                <TableRow key={role._id} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">
                                            {role.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={role.type}
                                            size="small"
                                            color={getTypeColor(role.type)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={role.level}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {role.description || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={role.permissions?.length || 0}
                                            size="small"
                                            color={role.permissions?.length > 0 ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <UserCountCell>
                                            <Tooltip title={`${role.userCount ?? 0} account(s) hold this role, not counting deleted ones. Click to view the users who hold this role.`}>
                                                <Chip
                                                    label={role.userCount ?? 0}
                                                    size="small"
                                                    color={(role.userCount ?? 0) > 0 ? 'primary' : 'default'}
                                                    variant="outlined"
                                                    onClick={() => handleViewUsersClick(role)}
                                                />
                                            </Tooltip>
                                            {(role.deletedUserCount ?? 0) > 0 && (
                                                <Tooltip title={`${role.deletedUserCount} deleted account(s) also hold this role. They cannot sign in, but they still have to be migrated before the role can be deleted.`}>
                                                    <Chip
                                                        label={`+${role.deletedUserCount} deleted`}
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleViewUsersClick(role)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </UserCountCell>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View the users who hold this role">
                                            <IconButton size="small" onClick={() => handleViewUsersClick(role)}>
                                                <PeopleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit role">
                                            <IconButton size="small" onClick={() => handleEditClick(role)} color="primary">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete role">
                                            <IconButton size="small" onClick={() => handleDeleteClick(role)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <RoleDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSave}
                role={selectedRole}
                mode={dialogMode}
            />

            {/* roleForUsers is kept after close so the dialog's contents do not change mid exit-transition,
                and the key remounts it per role so a newly opened role never paints the previous role's rows */}
            <RoleUsersDialog
                key={roleForUsers?._id || 'none'}
                open={usersDialogOpen}
                onClose={() => setUsersDialogOpen(false)}
                role={roleForUsers}
                onRoleChanged={loadRoles}
            />

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                message={`Are you sure you want to delete the role "${roleToDelete?.name}"? This action cannot be undone.`}
            />

            <Dialog
                open={migrateDialogOpen}
                onClose={() => setMigrateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Migrate users before deleting</DialogTitle>
                <DialogContent>
                    <MigrateDialogText>
                        {migrateUserCount} user{migrateUserCount === 1 ? '' : 's'} still hold the role
                        "{roleToDelete?.name}"
                        {migrateDeletedCount > 0 && ` (${migrateUserCount - migrateDeletedCount} active, ${migrateDeletedCount} deleted — deleted accounts still reference the role, so they have to move too)`}.
                        Pick another {roleToDelete?.type} role to move them to, then proceed with the deletion.
                    </MigrateDialogText>
                    <FormControl fullWidth>
                        <InputLabel id="migrate-to-label">Migrate to role</InputLabel>
                        <Select
                            labelId="migrate-to-label"
                            value={migrateToId}
                            label="Migrate to role"
                            onChange={(e) => setMigrateToId(e.target.value)}
                        >
                            {migrationCandidates.length === 0 ? (
                                <MenuItem value="" disabled>
                                    No other {roleToDelete?.type} roles available
                                </MenuItem>
                            ) : (
                                migrationCandidates.map((r) => (
                                    <MenuItem key={r._id} value={r._id}>
                                        {r.name} (level {r.level})
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMigrateDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!migrateToId}
                        onClick={handleConfirmMigrate}
                    >
                        Migrate &amp; Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </AdminPageContainer>
    );
};

export default AdminRoles;
